import {
  deprecatedFlagFilters,
  deprecatedSortOptions,
  enableFlagCleanupStatesFilter,
  flagListLimitGuardrail,
  getFlagListPageSize,
  isABTestingEnabled,
  isCodeReferencesEnabled,
  isDataExportEnabled,
  isDisableCodeReferencesWithOverrideEnabled,
} from '@gonfalon/dogfood-flags/dogfood-flags';
import invariant from 'tiny-invariant';

import { ApiVersions } from '../api-versions';
import type { GetFeatureFlagsOpenAPIQueryParams } from '../generated/operations/raw-openapi-query-params-for-serializers';

export interface GetFeatureFlagsQueryParams extends Omit<
  GetFeatureFlagsOpenAPIQueryParams,
  'env' | 'sort' | 'state' | 'filter' | 'expand'
> {
  env?: string[];
  sort?:
    | 'creationDate'
    | '-creationDate'
    | 'key'
    | '-key'
    | 'maintainerId'
    | '-maintainerId'
    | 'name'
    | '-name'
    | 'tags'
    | '-tags'
    | 'targetingModifiedDate'
    | '-targetingModifiedDate'
    | 'type'
    | '-type';
  filter?: {
    query?: string;
    type?: 'permanent' | 'temporary';
    status?: 'new' | 'inactive' | 'active' | 'launched';
    tags?: string[];
    hasExperiment?: boolean;
    hasDataExport?: boolean; // deprecated filter (v4 of the API)
    evaluated?: { after: number };
    maintainerId?: string;
    targeting?: 'on' | 'off';
    maintainerTeamKey?: string;
    contextKindTargeted?: string;
    contextKindsEvaluated?: string[];
    creationDate?: { before: number };
    segmentTargeted?: string;
    codeReferences?: { min?: number; max?: number };
    followerId?: string;
    excludeSettings?: Array<'prerequisites' | 'on' | 'targets' | 'rules' | 'fallthrough' | 'offVariation'>;
    sdkAvailability?: 'anyClient' | 'server' | 'mobile' | 'client';
    filterEnv?: string;
    view?: string;
    releasePipeline?: string;
    applicationEvaluated?: string;
    state?: 'live' | 'archived' | 'deprecated';
    staleStates?: 'readyForCodeRemoval' | 'readyToArchive';
    purpose?: 'holdout' | 'migration' | '!(holdout)' | '!(migration)' | 'all';
    guardedRollout?: 'any' | 'monitoring' | 'regressed' | 'rolledBack' | 'rolledBackAuto' | 'completed' | 'archived';
  };
  expand?: Array<'measuredRolloutStatus' | 'processes' | 'archiveChecks' | 'views'>;
}

function limitIsInRange(limit: number) {
  const clamped = Math.max(1, Math.min(limit || getFlagListPageSize(), getFlagListPageSize()));
  return clamped === limit;
}

export function querySerializer(params: GetFeatureFlagsQueryParams) {
  const searchParams = new URLSearchParams();

  const limit = params.limit;
  if (flagListLimitGuardrail()) {
    if (limit !== undefined) {
      invariant(limitIsInRange(limit), `Limit must be between 1 and ${getFlagListPageSize()}`);
      searchParams.set('limit', String(limit));
    }
  } else {
    if ((params.limit ?? 0) > 0) {
      searchParams.set('limit', String(params.limit));
    }
  }

  if (params.env) {
    for (const env of params.env) {
      searchParams.append('env', env);
    }
  }

  if ((params.offset ?? 0) > 0) {
    searchParams.set('offset', String(params.offset));
  }

  if (params.archived !== undefined) {
    searchParams.set('archived', String(params.archived));
  }

  if (params.compare !== undefined) {
    searchParams.set('compare', String(params.compare));
  }

  if (params.summary !== undefined) {
    searchParams.set('summary', String(params.summary));
  }

  if (params.sort) {
    const isDeprecatedTargetingSort = ['targetingModifiedDate', '-targetingModifiedDate'].includes(params.sort);
    const skipDeprecatedSort = isDeprecatedTargetingSort && !deprecatedSortOptions().targetingModifiedDate;

    if (!skipDeprecatedSort) {
      searchParams.set('sort', params.sort);
    }
  }

  if (params.filter) {
    const serializedFilter = serializeFlagListFilterParam(params.filter);
    if (serializedFilter) {
      searchParams.set('filter', serializedFilter);
    }
  }

  if (params?.expand) {
    searchParams.append('expand', params.expand.join(','));
  }

  return searchParams.toString();
}

export function serializeFlagListFilterParam(value: GetFeatureFlagsQueryParams['filter'] = {}) {
  const filter: string[] = [];

  if (value.query) {
    filter.push(`query:${encodeURIComponent(value.query)}`);
  }

  if (value.sdkAvailability) {
    filter.push(`sdkAvailability:${value.sdkAvailability}`);
  }

  if (value.filterEnv) {
    filter.push(`filterEnv:${value.filterEnv}`);
  }

  if (value.view) {
    filter.push(`view:${value.view}`);
  }

  if (deprecatedFlagFilters().contextKindTargeted && value.contextKindTargeted) {
    filter.push(`contextKindTargeted:${value.contextKindTargeted}`);
  }

  if (value.maintainerId) {
    filter.push(`maintainerId:${value.maintainerId}`);
  }

  if (value.maintainerTeamKey) {
    filter.push(`maintainerTeamKey:${value.maintainerTeamKey}`);
  }

  if (deprecatedFlagFilters().followerId && value.followerId) {
    filter.push(`followerId:${value.followerId}`);
  }

  if (deprecatedFlagFilters().hasExperiment && value.hasExperiment !== undefined) {
    filter.push(`hasExperiment:${String(value.hasExperiment)}`);
  }

  if (deprecatedFlagFilters().hasDataExport && isDataExportEnabled()) {
    if (value.hasDataExport !== undefined) {
      filter.push(`hasDataExport:${String(value.hasDataExport)}`);
    }
  }

  if (
    deprecatedFlagFilters().codeReferences &&
    !isDisableCodeReferencesWithOverrideEnabled() &&
    isCodeReferencesEnabled()
  ) {
    if (value.codeReferences !== undefined) {
      const { min, max } = value.codeReferences;
      if (min !== undefined) {
        filter.push(`codeReferences.min:${min}`);
      }

      if (max !== undefined) {
        filter.push(`codeReferences.max:${max}`);
      }
    }
  }

  if (value.evaluated) {
    filter.push(`evaluated:${JSON.stringify(value.evaluated)}`);
  }

  if (value.creationDate) {
    filter.push(`creationDate:${JSON.stringify(value.creationDate)}`);
  }

  if (value.tags && value.tags.length > 0) {
    filter.push(`tags:${value.tags.map(encodeURIComponent).join('+')}`);
  }

  if (value.type) {
    filter.push(`type:${value.type}`);
  }

  if (deprecatedFlagFilters().targeting && value.targeting) {
    filter.push(`targeting:${value.targeting}`);
  }

  if (deprecatedFlagFilters().status && value.status) {
    filter.push(`status:${value.status}`);
  }

  if (value.segmentTargeted) {
    filter.push(`segmentTargeted:${value.segmentTargeted}`);
  }

  if (
    deprecatedFlagFilters().contextKindsEvaluated &&
    value.contextKindsEvaluated &&
    value.contextKindsEvaluated.length > 0
  ) {
    filter.push(`contextKindsEvaluated:${value.contextKindsEvaluated.join('+')}`);
  }

  if (value.excludeSettings && value.excludeSettings.length > 0) {
    filter.push(`excludeSettings:${value.excludeSettings.join('+')}`);
  }

  if (value.releasePipeline) {
    filter.push(`releasePipeline:${value.releasePipeline}`);
  }

  if (value.applicationEvaluated) {
    filter.push(`applicationEvaluated:${value.applicationEvaluated}`);
  }

  if (value.state) {
    filter.push(`state:${value.state}`);
  }

  if (enableFlagCleanupStatesFilter() && value.staleStates) {
    filter.push(`staleState:${value.staleStates}`);
  }

  let purposeFilter = value.purpose ?? '';
  if (isABTestingEnabled()) {
    purposeFilter = purposeFilter ? `${purposeFilter}+!(holdout)` : '!(holdout)';
  }
  if (purposeFilter) {
    filter.push(`purpose:${purposeFilter}`);
  }

  if (value.guardedRollout !== undefined) {
    filter.push(`guardedRollout:${value.guardedRollout}`);
  }

  // aesthetics…
  filter.sort();

  return filter.join(',');
}

export const apiVersion = ApiVersions.V4;
